import React, { useState, useEffect } from "react";
import "../css/dashboard.css";
import ProductSearch from "../components/ProductSearch";
import ProductTable from "../components/ProductTable";
import PaymentSummary from "../components/Paymentsummary";
import ControlPanel from "../components/ControlPanel";

import TransactionModal from "../components/TransactionModal";
import DiscountModal from "../components/DiscountModal";

import { getEligiblePromotionTickets } from "../utils/Promotion"; // 🔧 helper
import ReceiptOptions from "../utils/receiptoptions";
import { toast } from "react-toastify";

import {
  LayoutDashboard,
  ShoppingCart,
  Wallet,
  Star,
  CreditCard,
  Activity,
  Users,
  Boxes,
  BarChart3,
  Radio,
  Search,
  Receipt,
  PanelLeft,
} from "lucide-react";


function OpeningBalanceModal({ onConfirm }) {
  const [balances, setBalances] = useState({
    Cash: "",
    MMG: "",
    Digicel: "",
    Superbet: "",
  });
  const [loading, setLoading] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [username, setUsername] = useState("");

  // ✅ Unified API helper (works in all builds)
  const RAW_API_BASE =
    (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
    process.env.REACT_APP_API_BASE ||
    "https://pos-backend-58o9.onrender.com";

  const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");
  const api = (path) =>
    `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;

  const getAuth = () => {
    const raw =
      sessionStorage.getItem("rcs_auth") || localStorage.getItem("rcs_auth");
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const authHeader = () => {
    const s = getAuth();
    return s?.access_token ? { Authorization: `Bearer ${s.access_token}` } : {};
  };

  useEffect(() => {
    const auth = getAuth();
    const name =
      auth?.user?.name ||
      auth?.user?.username ||
      auth?.user?.email?.split("@")[0] ||
      "User";
    setUsername(name.charAt(0).toUpperCase() + name.slice(1));
  }, []);

  // 🚀 Auto-fetch previous day closing when modal opens
  useEffect(() => {
    const fetchPrevDay = async () => {
      try {
        const res = await fetch(api("/balances/opening/auto"), {
          headers: {
            "Content-Type": "application/json",
            ...authHeader(),
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        if (data.success && Array.isArray(data.balances) && data.balances.length) {
          const prev = {};
          data.balances.forEach((b) => {
            prev[b.account] = b.opening_balance || 0;
          });
          setBalances((old) => ({ ...old, ...prev }));
          setAutoFilled(true);
          toast.info("✅ Auto-filled from yesterday’s closing balances");
        } else {
          setAutoFilled(false);
          toast.warn("⚠️ No previous closing balances found");
        }
      } catch (err) {
        console.error("Failed to load previous closing:", err);
        toast.error("Could not fetch previous balances.");
      }
    };

    fetchPrevDay();
  }, []);

  const handleChange = (key, value) => {
    setBalances((prev) => ({ ...prev, [key]: value }));
  };

  const submitOpeningBalances = async () => {
    const payload = Object.entries(balances)
      .filter(([_, val]) => val !== "")
      .map(([account, opening_balance]) => ({
        account,
        opening_balance: parseFloat(opening_balance) || 0,
      }));

    if (payload.length === 0) {
      alert("Please enter at least one balance.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(api("/balances/opening"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ accounts: payload }),
      });

      if (!res.ok) throw new Error("Failed to save balances");

      toast.success("✅ Opening balances saved!");
      onConfirm();
    } catch (err) {
      console.error("Error submitting balances:", err);
      toast.error("Failed to save opening balances.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2 style={{ textAlign: "center", marginBottom: "10px", color: "#2c3e50" }}>
          👋 Hello, {username}!
        </h2>

        <h3>Confirm Opening Balances</h3>
        <p>
          {autoFilled
            ? "Yesterday’s closing balances were auto-filled below. Review or adjust if needed."
            : "Enter today’s starting balance for each account:"}
        </p>

        {Object.keys(balances).map((acc) => (
          <div key={acc} style={{ marginBottom: "8px" }}>
            <label>{acc}</label>
            <input
              type="number"
              placeholder={`Enter ${acc} balance`}
              value={balances[acc]}
              onChange={(e) => handleChange(acc, e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                marginTop: "4px",
                background: autoFilled ? "#eaffea" : "#fff",
                border: "1px solid #ccc",
                borderRadius: "5px",
              }}
            />
          </div>
        ))}

        <div className="modal-actions">
          <button
            onClick={submitOpeningBalances}
            disabled={loading}
            className="save-btn"
          >
            {loading ? "Saving..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}



// Normalized API base: works for CRA (REACT_APP_) and Vite (VITE_)
// and tolerates values with or without trailing "/api" and slashes.
const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");
const api = (path) => `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;

console.log("[Dashboard] API_BASE =", API_BASE);


const safeJson = (s) => { try { return JSON.parse(s); } catch { return null; } };
const getAuth = () => {
  const raw =
    sessionStorage.getItem("auth") ||
    localStorage.getItem("auth") ||
    sessionStorage.getItem("rcs_auth") ||
    localStorage.getItem("rcs_auth");
  const obj = raw && safeJson(raw);
  if (obj && (obj.access_token || obj.token)) {
    return { access_token: obj.access_token || obj.token, user: obj.user || obj.profile || null };
  }
  return {};
};
const authHeader = () => {
  const { access_token } = getAuth();
  return access_token ? { Authorization: `Bearer ${access_token}` } : {};
};

const Dashboard = ({
  cart,
  setCart,
  searchTerm,
  setSearchTerm,
  //addToCart
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [showDiscountModal, setShowDiscountModal] = useState(false)
  
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [promotions, setPromotions] = useState([]);
  const [showReceiptOptions, setShowReceiptOptions] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [amountTendered, setAmountTendered] = useState("");
  const [products, setProducts] = useState([]);
  const [availablePoints, setAvailablePoints] = useState(0);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [usePoints, setUsePoints] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedReceiver, setSelectedReceiver] = useState(null);
 
const [showTransactionModal, setShowTransactionModal] =
  useState(false);

  const [ferryDetails, setFerryDetails] = useState({
  booking: "",
  payment: "",
  vehicle_no: "",
  vehicle_type: "",
  route: "",
  travelDate: "",
  roundTrip: false,
  returnDate: null
});


  const [barcodeBuffer, setBarcodeBuffer] = useState("");
    let barcodeTimer = null;
const [searchInput, setSearchInput] = useState("");
const [receiverInfo, setReceiverInfo] = useState({
  name: "",
  trans_id: "",
  acc_number: ""
});

const [showOpeningModal, setShowOpeningModal] = useState(false);

useEffect(() => {
  const API_BASE =
    (process.env.REACT_APP_API_BASE || "https://pos-backend-58o9.onrender.com").replace(/\/+$/, "");
  const token =
    JSON.parse(localStorage.getItem("rcs_auth") || sessionStorage.getItem("rcs_auth")) || {};

  if (!token?.access_token) return;

 fetch(api("/balances/opening/today"), {


    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token.access_token}` },
  })
    .then((res) => res.json())
    .then((data) => {
      // backend returns an array of balances, if it's empty => show modal
      if (!Array.isArray(data) || data.length === 0) {
        setShowOpeningModal(true);
      }
    })
    .catch((err) => {
      console.error("Failed to check opening balances:", err);
      setShowOpeningModal(true);
    });
}, []);


// ⛔ prevent duplicate submissions
const SUBMIT_COOLDOWN_MS = 1500;         // tweak if you want longer/shorter
const [isSubmittingSale, setIsSubmittingSale] = useState(false);
const [lastSubmitTs, setLastSubmitTs] = useState(0);

  const receiverRequiredProductIds = [7, 9, 10, 11, 18, 19, 21];
  const dualCustomerProducts = ["MMG Subscriber Deposit", "MMG Agent Transfer"];

const cartRequiresTwoCustomers = cart.some(item =>
  receiverRequiredProductIds.includes(item.id)
);


  const [customerDatabase, setCustomerDatabase] = useState([]);

const openTransaction = () => {

  if (!cart.length) {

    toast.warn("Cart is empty.");

    return;
  }

  setShowTransactionModal(true);
};



/* =====================================================
    HOTKEY → OPEN TRANSACTION TERMINAL
===================================================== */

useEffect(() => {

  const handleTransactionHotkey = (e) => {

    const isPlusKey =
      e.key === "+" ||
      e.key === "=" ||
      e.code === "NumpadAdd";

    if (!isPlusKey) return;

    // prevent typing into inputs
    const tag =
      document.activeElement?.tagName;

    const isTyping =
      tag === "INPUT" ||
      tag === "TEXTAREA";

    if (isTyping) return;

    e.preventDefault();

    openTransaction();

  };

  window.addEventListener(
    "keydown",
    handleTransactionHotkey
  );

  return () =>
    window.removeEventListener(
      "keydown",
      handleTransactionHotkey
    );

}, [cart]);



const handleFinish = async () => {
if (!cart.length) { toast.warn("Cart is empty."); return; }
if (!paymentMethod) { toast.warn("Select a payment method."); return; }


// 🚧 prevent rapid double-submits
const now = Date.now();
if (isSubmittingSale || now - lastSubmitTs < SUBMIT_COOLDOWN_MS) {
  return; // ignore duplicate trigger
}
setIsSubmittingSale(true);
setLastSubmitTs(now);


  // Check for insufficient stock before proceeding
  const outOfStockItems = cart.filter(
    (item) => item.uses_stock && item.quantity > item.product_quantity
  );

  if (outOfStockItems.length > 0) {
    toast.error("Some items have insufficient stock. Please adjust and try again.");

    return;
  }

 const subtotal = cart.reduce((sum, item) => {
  if (item.exclude_from_total) return sum;

  return sum + (item.price * item.quantity);
}, 0);


const totalCommission = cart.reduce((sum, item) => {
  return item.is_commission
    ? sum + (item.price * item.quantity)
    : sum;
}, 0);

  const discount = cart.reduce((sum, item) => {
    if (!item.discount || isNaN(item.discount)) return sum;
    if (item.discountType === "percent") {
      return sum + (item.price * item.quantity) * (item.discount / 100);
    } else if (item.discountType === "cash") {
      return sum + parseFloat(item.discount);
    }
    return sum;
  }, 0);

const total = subtotal - discount;

const pointValue = 2;

const maxPointsAllowed =
  total > 0
    ? Math.floor(total / pointValue)
    : 0;

const pointsToApply = usePoints
  ? Math.min(pointsToRedeem, maxPointsAllowed)
  : 0;

const pointsDiscount = pointsToApply * pointValue;

// ✅ Allow negative totals
const finalTotal = total - pointsDiscount;



  console.log("🧾 Sale summary:", {
    total,
    pointsToApply,
    pointsDiscount,
    finalTotal
  });
  
  const name = cartRequiresTwoCustomers
  ? selectedSender?.name || "Unknown"
  : selectedCustomer?.name || "Unknown";

const rawPhone = cartRequiresTwoCustomers
  ? selectedSender?.phone || selectedSender?.contact || ""
  : selectedCustomer?.phone || selectedCustomer?.contact || "";

  const customerPhone = rawPhone
    ? (rawPhone.startsWith("+") ? rawPhone : "+592" + rawPhone)
    : null;

  const ticketCount = getEligiblePromotionTickets(finalTotal, promotions);

  try {
    //const pointsDiscount = pointsToApply * 2;
    //const finalTotal = Math.max(total - pointsDiscount, 0);

console.log("🧾 SALE PAYLOAD:", {
  cartRequiresTwoCustomers,
  customer_external_id: cartRequiresTwoCustomers
    ? selectedSender?.external_id
    : selectedCustomer?.external_id,
  customer_name: cartRequiresTwoCustomers
    ? selectedSender?.name
    : selectedCustomer?.name,
});

    // Step 1: Record the sale
const res = await fetch(api("/sales"), {
  method: "POST",
  headers: { "Content-Type": "application/json", ...authHeader() },
  body: JSON.stringify({
    customer_id: cartRequiresTwoCustomers ? selectedSender?.id : selectedCustomer?.id,
    customer_external_id: cartRequiresTwoCustomers
      ? selectedSender?.external_id
      : selectedCustomer?.external_id, // ✅ add this field
    customer_name: name,
    customer_phone: customerPhone,
    payment_method: paymentMethod,
    subtotal,
    discount,
    total: finalTotal,
    items,
    amount_tendered: amountTendered || null,
    change:
      paymentMethod === "Cash"
        ? Number.isFinite(Number(amountTendered))
          ? Number(amountTendered) - finalTotal
          : null
        : null,
    points_redeemed: pointsToApply,
    receiver_info: cartRequiresTwoCustomers
      ? {
          name: receiverInfo.name || null,
          trans_id: receiverInfo.trans_id || null,
          acc_number: receiverInfo.acc_number || null,
        }
      : null,
    tickets_earned: ticketCount,
ferry_info: cart.some((item) => item.id === 8)
  ? {
      booking: ferryDetails.booking,
      payment: ferryDetails.payment,
      vehicle_no: ferryDetails.vehicle_no,   // ✅ FIXED
      vehicle_type: ferryDetails.vehicle_type, // ✅ FIXED
      route: ferryDetails.route,
      travel_date: ferryDetails.travelDate,
      round_trip: ferryDetails.roundTrip,
      return_date: ferryDetails.roundTrip ? ferryDetails.returnDate : null
    }
  : null
  }),
});


    if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`Failed to record sale (${res.status}) ${msg}`);
   }

    const sale = await res.json();


// ----------------------------------------------------
//  RCS → Receivables: Pending Credit Request Logic
//  Triggered when item ID 17 ("Account Paid") is in the cart
// ----------------------------------------------------
try {
  const accountPaidItems = cart.filter((item) => item.id === 17);

  if (accountPaidItems.length > 0) {
    const totalCredit = accountPaidItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const customerExternalId =
      cartRequiresTwoCustomers
        ? selectedSender?.external_id
        : selectedCustomer?.external_id;

    if (customerExternalId) {
const payload = {
  external_customer_id: customerExternalId,
  customer_name:
    cartRequiresTwoCustomers ? selectedSender?.name : selectedCustomer?.name,
  amount: totalCredit,
  sale_reference: sale.receipt_no,
  description: "POS Payment Credit"
};


      const creditRes = await fetch(
        "https://receivablesbackend.onrender.com/api/pending-credits",


        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!creditRes.ok) {
        console.error("Failed to send credit request:", creditRes.status);
        toast.error("Failed to send Credit Request to Receivables");
      } else {
        toast.success("Credit Request sent to Receivables!");
      }
    } else {
      console.log("ℹ️ No external_id for customer, credit not sent.");
    }
  }
} catch (err) {
  console.error("Error sending pending credit:", err);
}


    // Post-Transaction
// Post-Transaction: Add customer info for receipt
const senderInfo = cartRequiresTwoCustomers ? selectedSender : selectedCustomer;

sale.customer = {
  name: senderInfo?.name || "Unknown",
  phone: senderInfo?.phone || senderInfo?.contact || "-",
  address: senderInfo?.address || "-"
};


// 🎯 Calculate points earned and redeemed manually
// ✅ Calculate eligible total (exclude items flagged to not earn points)
const eligibleForPointsTotal = cart.reduce((sum, item) => {
  return item.exclude_from_points ? sum : sum + item.price * item.quantity;
}, 0);

// ✅ Calculate points only from eligible items
const pointsEarned = Math.floor(eligibleForPointsTotal / 1000);



const pointsRedeemed = usePoints ? pointsToRedeem : 0;


// 🧾 Add points to sale object for printing
const enrichedSale = {
  ...sale,
  points_earned: pointsEarned,
  points_redeemed: pointsRedeemed
};

// ✅ Trigger receipt
setLastSale(enrichedSale);
setShowReceiptOptions(true);
setShowTransactionModal(false);
toast.success("Sale completed and receipt generated!");


// 🔄 Sync stock to DB for stock-tracked items
try {
  await updateStock(
    cart
      .filter(i => i.uses_stock)
      .map(i => ({ product_id: i.product_id || i.id, quantity: i.quantity }))
  );
} catch (e) {
  console.error("Stock update failed after sale:", e);
  // optional: toast.warn("Sale saved, but failed to sync stock.");
}



// Reset
setCart([]);
setPaymentMethod("");
setSelectedCustomer(null);
setAvailablePoints(0);
setPointsToRedeem(0);
setAmountTendered("");
setSelectedSender(null);
setSelectedReceiver(null);
setShowTransactionModal(false);
setReceiverInfo({
  name: "",
  trans_id: "",
  acc_number: "",
});

setFerryDetails({
  booking: "",
  payment: "",
  vehicle_no: "",
  vehicle_type: "",
  route: "",
  travelDate: "",
  roundTrip: false,
  returnDate: null
});
setTimeout(() => setIsSubmittingSale(false), SUBMIT_COOLDOWN_MS);

  } catch (err) {
    console.error("❌ Sale failed:", err);

    try {
      const errorText = await err.response?.text?.();
      console.error("🔍 Backend Error Response:", errorText);
    } catch (e) {
      console.warn("Failed to parse backend error");
    } finally {
  // release lock after a small cool-down
  setTimeout(() => setIsSubmittingSale(false), SUBMIT_COOLDOWN_MS);
}
    
    toast.error("Failed to record sale. Please try again.");
    
  }
  
};

useEffect(() => {
  const handleBarcodeInput = (e) => {
    // Barcode scanners send a quick stream of characters ending with Enter
    if (barcodeTimer) clearTimeout(barcodeTimer);

    if (e.key === "Enter") {
      const code = barcodeBuffer.trim();
      if (code) setSearchTerm(code);

      setBarcodeBuffer("");
      return;
    }

    // Only capture digits or letters (for alphanumeric barcodes)
    if (/^[A-Za-z0-9]$/.test(e.key)) {
      setBarcodeBuffer(prev => prev + e.key);
    }

    // Reset buffer after short delay if no keys
    barcodeTimer = setTimeout(() => setBarcodeBuffer(""), 200);
  };

  window.addEventListener("keydown", handleBarcodeInput);
  return () => window.removeEventListener("keydown", handleBarcodeInput);
}, [barcodeBuffer]);





  useEffect(() => {
    fetch(api("/customers"), { headers: { ...authHeader() } })

      .then(res => res.json())
      .then(data => setCustomerDatabase(data))
      .catch(err => console.error("Failed to load customer data", err));
  }, []);
  
  useEffect(() => {
    fetch(api("/promotions"), { headers: { ...authHeader() } })

      .then(res => res.json())
      .then(data => setPromotions(data))
      .catch(err => console.error("Failed to load promotions", err));
  }, []);
  


const items = cart
  .filter(item => !item.exclude_from_total)
  .map(item => ({
    product: item.name,
    product_id: item.product_id || item.id,
    quantity: item.quantity,
    price: item.price
  }));


  
  useEffect(() => {
    const fetchPoints = async () => {
      const pointsCustomer = cartRequiresTwoCustomers ? selectedSender : selectedCustomer;
  
      if (pointsCustomer?.id) {
        try {
          const res = await fetch(api(`/loyalty/${pointsCustomer.id}`), { headers: { ...authHeader() } })

          const data = await res.json();
          setAvailablePoints(data.available_points || 0);
        } catch (err) {
          console.error("Failed to fetch loyalty points", err);
        }
      } else {
        setAvailablePoints(0);
      }
    };
  
    fetchPoints();
  }, [selectedCustomer, selectedSender, cartRequiresTwoCustomers]);
  



// 🧰 ADD THIS ABOVE addToCart
const dbStockOf = (p) => Number(p.product_quantity ?? p.available_quantity ?? p.quantity ?? 0);

// total reserved in the cart for a given product id (optionally exclude a line by index)
const reservedQty = (productId, excludeIndex = -1) =>
  cart.reduce((sum, item, idx) => (
    item.id === productId && idx !== excludeIndex
      ? sum + Number(item.quantity || 0)
      : sum
  ), 0);



  const addToCart = (product) => {
    const isMMGSubscriberDeposit = product.id === 9;
    const isChequeEncashing = product.id === 78;
    const isMMOOut = product.id === 18;
    const isSubscriberDepositInwards = product.id === 12;


// ==== MERGE by line signature (kept) ====
const norm = (n) => Number(n ?? 0).toFixed(2);
const sameLine = (a, b) =>
  a.id === b.id &&
  norm(a.price) === norm(b.price) &&
  (a.discountType || "") === (b.discountType || "") &&
  norm(a.discount) === norm(b.discount) &&
  (a.metaSource || "") === (b.metaSource || "");

// 👉 true stock on the product coming from DB
const dbStock = Number(product.product_quantity ?? product.quantity ?? 0);
const usesStock = Boolean(product.uses_stock);

// Try to find an **exact** line-match
const existingItem = cart.find((i) => sameLine(i, product));
let updatedCart;

// ✅ Aggregate stock guard across all lines of this product
if (usesStock) {
  const already = reservedQty(product.id); // sum of ALL lines for this product
  const newTotalReserved = already + 1;    // we’re adding exactly one unit here
  if (newTotalReserved > dbStock) {
    const left = Math.max(0, dbStock - already);
    toast.warn(left === 0 ? "This item is out of stock." : `Only ${left} left for this item.`);
    return;
  }
}


if (existingItem) {
  // increment only the exact-matching line
  updatedCart = cart.map((i) =>
    sameLine(i, product) ? { ...i, quantity: i.quantity + 1 } : i
  );
} else {
  // create a new line with stock metadata stored
  if (usesStock && dbStock < 1) {
    toast.warn("This item is out of stock.");
    return;
  }
  updatedCart = [
    ...cart,
    {
      ...product,
      product_id: product.id,
      quantity: 1,
      // 🔑 keep both for downstream checks/UI
      product_quantity: dbStock,      // source of truth for available stock
      available_quantity: dbStock,    // (legacy) kept so old code still works
      uses_stock: usesStock,
    },
  ];
}

  
// ✅ MMG Admin Charge Logic
if (isMMGSubscriberDeposit) {
  const totalAmount = product.price * (existingItem ? existingItem.quantity + 1 : 1);
  let adminFee = 0;

  if (totalAmount < 30000) {
    adminFee = totalAmount * 0.01; // ✅ NEW RULE (1%)
  } else if (totalAmount >= 30000 && totalAmount <= 50000) {
    adminFee = totalAmount * 0.015;
  } else if (totalAmount > 50000 && totalAmount <= 75000) {
    adminFee = totalAmount * 0.02;
  } else if (totalAmount > 75000 && totalAmount <= 100000) {
    adminFee = totalAmount * 0.025;
  } else if (totalAmount > 100000 && totalAmount <= 150000) {
    adminFee = totalAmount * 0.03;
  } else if (totalAmount > 150000) {
    adminFee = 1500 + (totalAmount * 0.03);
  }

  // Remove existing admin charge if any
  updatedCart = updatedCart.filter(item => item.id !== 13);

  // ✅ Only add if fee is greater than 0
  if (adminFee > 0) {
    updatedCart.push({
      id: 13,
      name: "Admin Charge",
      product_id: 13,
      type: "fee",
      quantity: 1,
      price: parseFloat(adminFee.toFixed(2)),
      exclude_from_points: true,
      product_code: "SR001",
    });
  }
}
  
    //Cheque
// ✅ Cheque Encashing Admin Charge Rule (handles negative outflow)
if (isChequeEncashing) {
  const nextQty = existingItem ? existingItem.quantity + 1 : 1;

  // Cheque amounts are negative → take absolute value for calculation
  const chequeAmount = Math.abs(product.price) * nextQty;

  let adminFee = 0;
  if (chequeAmount <= 100000) {
    // Flat fee for cheques up to 100,000
    adminFee = 2000;
  } else {
    // 2% for cheques above 100,000
    adminFee = chequeAmount * 0.02;
  }

  // Remove any existing admin charge line
  updatedCart = updatedCart.filter(item => item.id !== 13);

  // ✅ Always add as positive charge (RCS earns this fee)
  if (adminFee > 0) {
    updatedCart.push({
      id: 13,
      name: "Admin Charge",
      product_id: 13,
      type: "fee",
      quantity: 1,
      price: parseFloat(adminFee.toFixed(2)), // positive fee
      exclude_from_points: true,
      product_code: "SR001",
    });
  }
}


    
// MMO out
if (isMMOOut) {
  const quantity = existingItem ? existingItem.quantity + 1 : 1;
  const totalAmount = product.price * quantity;

  let adminFee = 0;
  let mmgFee = 0;

  if (totalAmount <= 5000) {
    adminFee = 180;
    mmgFee = 120;
  } else if (totalAmount <= 10000) {
    adminFee = 380;
    mmgFee = 120;
  } else if (totalAmount <= 25000) {
    adminFee = 880;
    mmgFee = 120;
  } else if (totalAmount <= 40000) {
    adminFee = 1200;
    mmgFee = totalAmount * 0.005;
  } else if (totalAmount <= 75000) {
    adminFee = 2500;
    mmgFee = totalAmount * 0.005;
  } else if (totalAmount <= 100000) {
    adminFee = totalAmount * 0.035;
    mmgFee = totalAmount * 0.005;
  } else {
    adminFee = (totalAmount * 0.035) + 1500;
    mmgFee = totalAmount * 0.005;
  }

  // Remove previous Admin + MMG Fees
  updatedCart = updatedCart.filter(
    item => item.id !== 13 && item.id !== 23
  );

  // ✅ Push Admin Charge
  updatedCart.push({
    id: 13,
    name: "Admin Charge",
    product_id: 13,
    type: "fee",
    quantity: 1,
    price: parseFloat(adminFee.toFixed(2)),
    exclude_from_points: true,
    product_code: "SR001",
  });

  // ✅ Push MMG Fees (ID 23)
  updatedCart.push({
    id: 23,
    name: "MMG Fees",
    product_id: 23,
    type: "fee",
    quantity: 1,
    price: parseFloat(mmgFee.toFixed(2)),
    exclude_from_points: true,
    product_code: "SR009",
  });
}

// ✅ MMG WITHDRAWAL ADMIN CHARGE (1%)
if (product.id === 11) {

  const totalAmount =
    Math.abs(
      product.price *
      (
        existingItem
          ? existingItem.quantity + 1
          : 1
      )
    );

  const adminFee =
    totalAmount * 0.01;

  // Remove previous admin charge
  updatedCart =
    updatedCart.filter(
      item => item.id !== 13
    );

  // Add updated admin charge
  if (adminFee > 0) {

    updatedCart.push({
      id: 13,
      name: "Admin Charge",
      product_id: 13,
      type: "fee",
      quantity: 1,

      price: parseFloat(
        adminFee.toFixed(2)
      ),

      exclude_from_points: true,

      product_code: "SR001",
    });
  }
}



// Deposit inward
// ✅ SUBSCRIBER DEPOSIT INWARDS ADMIN CHARGE (REVISED)

if (isSubscriberDepositInwards) {
  const totalAmount = Math.abs(
    product.price * (existingItem ? existingItem.quantity + 1 : 1)
  );

  let adminFee = 0;

  if (totalAmount <= 5000) {
    adminFee = 100;

  } else if (totalAmount <= 10000) {
    adminFee = 250;

  } else if (totalAmount <= 20000) {
    adminFee = 500;

  } else if (totalAmount <= 30000) {
    adminFee = 1000;

  } else if (totalAmount <= 50000) {
    adminFee = 500 + (totalAmount * 0.015);

  } else if (totalAmount <= 70000) {
    adminFee = 1000 + (totalAmount * 0.015);

  } else if (totalAmount <= 90000) {
    adminFee = 1500 + (totalAmount * 0.015);

  } else if (totalAmount <= 120000) {
    adminFee = 1500 + (totalAmount * 0.02);

  } else if (totalAmount <= 150000) {
    adminFee = totalAmount * 0.03;

  } else {
    adminFee = totalAmount * 0.035;
  }

  // Remove existing admin charge
  updatedCart = updatedCart.filter(item => item.id !== 13);

  // Add updated fee
  if (adminFee > 0) {
    updatedCart.push({
      id: 13,
      name: "Admin Charge",
      product_id: 13,
      type: "fee",
      quantity: 1,
      price: parseFloat(adminFee.toFixed(2)),
      exclude_from_points: true,
      product_code: "SR001",
    });
  }
}

// 🔁 DIGICEL WHOLESALE → 5% COMMISSION LINE
{
  const WHOLESALE_ID = 20;           // "Digicel Wholesale"
  const COMMISSION_ID = 27;          // ✅ correct DB ID
  const COMMISSION_NAME = "Digicel Wholesale Commission";
  const COMMISSION_RATE = 0.05;      // 5%


  // Recalculate whenever product 20 is added or already present
  const hasWholesale = product.id === WHOLESALE_ID || updatedCart.some(i => i.id === WHOLESALE_ID);

  if (hasWholesale) {
    // Find wholesale line after the current add/increment
    const wholesale = updatedCart.find(i => i.id === WHOLESALE_ID);

    // Remove any previous commission line so we can re-add with fresh value
    updatedCart = updatedCart.filter(i => i.id !== COMMISSION_ID);

    if (wholesale) {
      const qty = Number(wholesale.quantity || 0);
      const unitPrice = Number(wholesale.price || 0);
      const commission = +(qty * unitPrice * COMMISSION_RATE).toFixed(2);

      if (commission > 0) {
updatedCart.push({
  id: COMMISSION_ID,
  product_id: COMMISSION_ID,
  name: COMMISSION_NAME,
  type: "commission",
  quantity: 1,
  price: commission,

  exclude_from_points: true,
  exclude_from_total: true,      // ✅ Customer does NOT pay this
  is_commission: true            // ✅ For reporting/display
});
      }
    }
  }
}


    setCart(updatedCart);
  };
  
  
  const updateStock = async (cart) => {
    try {
      const response = await fetch(api("/products/update-stock"), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ cart }),
      });
  
      if (!response.ok) {
        throw new Error("Failed to update stock");
      }
  
      console.log("Stock updated successfully.");
  
      // ✅ Fetch latest product data to reflect updated quantities
      const updatedResponse = await fetch(api("/products"), { headers: { ...authHeader() } });

      const updatedData = await updatedResponse.json();
      setProducts(updatedData); // ✅ Update products with latest data
      setCart([]);
      setSelectedProduct(null);
    } catch (error) {
      console.error("Error updating stock:", error);
    }
  };
  

const changeQuantity = (lineIndex, newQty) => {
  setCart(prev => {
    const next = [...prev];
    const item = next[lineIndex];
    const uses = Boolean(item.uses_stock);
    const stock = dbStockOf(item);
    const wants = Math.max(1, Number(newQty || 1));

    if (uses) {
      const already = prev.reduce((sum, it, idx) =>
        it.id === item.id && idx !== lineIndex ? sum + Number(it.quantity || 0) : sum
      , 0);
      if (already + wants > stock) {
        const left = Math.max(0, stock - already);
        toast.warn(left === 0 ? "No units left for this item." : `Only ${left} left for this item.`);
        return prev;
      }
    }

    next[lineIndex] = { ...item, quantity: wants };
    return next;
  });
};

const incQuantity = (lineIndex) => {
  setCart(prev => {
    const next = [...prev];
    const item = next[lineIndex];
    const uses = Boolean(item.uses_stock);
    const stock = dbStockOf(item);
    const wants = Number(item.quantity || 1) + 1;

    if (uses) {
      const already = prev.reduce((sum, it, idx) =>
        it.id === item.id && idx !== lineIndex ? sum + Number(it.quantity || 0) : sum
      , 0);
      if (already + wants > stock) {
        const left = Math.max(0, stock - already);
        toast.warn(left === 0 ? "No units left for this item." : `Only ${left} left for this item.`);
        return prev;
      }
    }

    next[lineIndex] = { ...item, quantity: wants };
    return next;
  });
};


const decQuantity = (lineIndex) => {
  setCart(prev => {
    const next = [...prev];
    const item = next[lineIndex];
    const qty = Math.max(1, Number(item.quantity || 1) - 1);
    next[lineIndex] = { ...item, quantity: qty };
    return next;
  });
};



const searchByBarcode = async (code) => {
  try {
    const res = await fetch(api(`/products/barcode/${code}`), {
      headers: { ...authHeader() },
    });

    if (res.ok) {
      const product = await res.json();
      addToCart(product);
      toast.success(`✅ ${product.name} added via barcode`);
    } else {
      toast.error("❌ Product not found for scanned barcode");
    }
  } catch (err) {
    console.error("Barcode search failed:", err);
    toast.error("Error scanning barcode.");
  }
};

return (
<div className={`modern-pos-shell ${
  showDiscountModal || showTransactionModal
    ? "modal-active"
    : ""
}`}>
    {/* =========================
        OPENING BALANCE
    ========================== */}
    {showOpeningModal && (
      <OpeningBalanceModal
        onConfirm={() => {
          toast.success("Opening balances saved!");
          setShowOpeningModal(false);
          window.location.reload();
        }}
      />
    )}

    {/* =========================
        MAIN CONTENT
    ========================== */}

<TransactionModal
  isOpen={showTransactionModal}
  onClose={() =>
    setShowTransactionModal(false)
  }

  cart={cart}
  setCart={setCart}

  selectedCustomer={selectedCustomer}
  setSelectedCustomer={setSelectedCustomer}

  selectedSender={selectedSender}
  setSelectedSender={setSelectedSender}

  selectedReceiver={selectedReceiver}
  setSelectedReceiver={setSelectedReceiver}

  paymentMethod={paymentMethod}
  setPaymentMethod={setPaymentMethod}

  amountTendered={amountTendered}
  setAmountTendered={setAmountTendered}

  availablePoints={availablePoints}

  usePoints={usePoints}
  setUsePoints={setUsePoints}

  pointsToRedeem={pointsToRedeem}
  setPointsToRedeem={setPointsToRedeem}

  ferryDetails={ferryDetails}
  setFerryDetails={setFerryDetails}

  receiverInfo={receiverInfo}
  setReceiverInfo={setReceiverInfo}

  customerDatabase={customerDatabase}

  onFinish={handleFinish}

  cartRequiresTwoCustomers={
    cartRequiresTwoCustomers
  }

  onIncQty={incQuantity}
  onDecQty={decQuantity}
  onSetQty={changeQuantity}
/>



    <main className="modern-pos-main">

      {/* =========================
          TOP STRIP
      ========================== */}
      <section className="modern-pos-top-strip">

        <div className="modern-pos-live-status">
          <div className="modern-live-dot" />
          <span>Live Terminal</span>
        </div>

      </section>

      {/* =========================
          METRICS
      ========================== */}
      <section className="modern-pos-metrics-grid">

        {/* ITEMS */}
        <div className="modern-metric-card">

          <div className="modern-metric-content">

            <span className="modern-metric-label">
              Cart Items
            </span>

            <h2>
              {cart.reduce(
                (sum, item) => sum + item.quantity,
                0
              )}
            </h2>

          </div>

          <div className="modern-metric-icon">
            <ShoppingCart
              size={24}
              strokeWidth={2.3}
            />
          </div>

        </div>

        {/* SUBTOTAL */}
        <div className="modern-metric-card">

          <div className="modern-metric-content">

            <span className="modern-metric-label">
              Subtotal
            </span>

            <h2>
              $
{
cart
  .reduce((sum, item) => {
    if (item.exclude_from_total) return sum;
    return sum + item.price * item.quantity;
  }, 0)
  .toLocaleString()
}
            </h2>

          </div>

          <div className="modern-metric-icon">
            <Wallet
              size={24}
              strokeWidth={2.3}
            />
          </div>

        </div>

        {/* LOYALTY */}
        <div className="modern-metric-card">

          <div className="modern-metric-content">

            <span className="modern-metric-label">
              Loyalty Points
            </span>

            <h2>{availablePoints}</h2>

          </div>

          <div className="modern-metric-icon">
            <Star
              size={24}
              strokeWidth={2.3}
            />
          </div>

        </div>

        {/* PAYMENT */}
        <div className="modern-metric-card">

          <div className="modern-metric-content">

            <span className="modern-metric-label">
              Payment Method
            </span>

            <h2>
              {paymentMethod || "Pending"}
            </h2>

          </div>

          <div className="modern-metric-icon">
            <CreditCard
              size={24}
              strokeWidth={2.3}
            />
          </div>

        </div>

      </section>

      {/* =========================
          WORKSPACE
      ========================== */}
      <section className="modern-pos-workspace">

        {/* =========================
            LEFT SIDE
        ========================== */}
        <div className="modern-pos-left-column">

          {/* SEARCH */}
          <div className="modern-pos-card modern-search-card">

            <div className="modern-section-header compact">

              <div>
                <h3>Product Search</h3>
              </div>

              <Search
                size={18}
                className="modern-header-icon"
              />

            </div>

            <ProductSearch
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              addToCart={addToCart}
            />

          </div>

          {/* CART */}
          <div className="modern-pos-card modern-table-card">

            <div className="modern-section-header compact">

              <div>
                <h3>Current Cart</h3>
              </div>

              <Receipt
                size={18}
                className="modern-header-icon"
              />

            </div>

            <ProductTable
              cart={cart}
              setCart={setCart}
              setSelectedProduct={setSelectedProduct}
              selectedProduct={selectedProduct}
              onIncQty={incQuantity}
              onDecQty={decQuantity}
              onSetQty={changeQuantity}
            />

          </div>

        </div>

        {/* =========================
            RIGHT SIDE
        ========================== */}
        <div className="modern-pos-right-column">

          {/* SUMMARY */}
          <div className="modern-pos-card modern-summary-card">

            <div className="modern-section-header compact">

              <div>
                <h3>Payment Summary</h3>
              </div>

              <Wallet
                size={18}
                className="modern-header-icon"
              />

            </div>

            <PaymentSummary
              cart={cart}
              discount={0}
              tax={0}
            />

          </div>

          {/* CONTROLS */}
          <div className="modern-pos-card modern-controls-card">

            <div className="modern-section-header compact">

              <div>
                <h3>Controls</h3>
              </div>

              <Activity
                size={18}
                className="modern-header-icon"
              />

            </div>

            <ControlPanel
              selectedProduct={selectedProduct}
              cart={cart}
              setCart={setCart}
              processPayment={openTransaction}
              openDiscountModal={() =>
                setShowDiscountModal(true)
              }
              onIncQty={incQuantity}
              onDecQty={decQuantity}
              onSetQty={changeQuantity}
            />

          </div>


        </div>

      </section>

      {/* =========================
          MODALS
      ========================== */}

      {showDiscountModal &&
        selectedProduct && (
          <DiscountModal
            product={selectedProduct}
            onClose={() =>
              setShowDiscountModal(false)
            }
            onSave={(updatedProduct) => {
              const updatedCart = cart.map(
                (item) =>
                  item.id ===
                  updatedProduct.id
                    ? updatedProduct
                    : item
              );

              setCart(updatedCart);
            }}
          />
        )}

      {showReceiptOptions &&
        lastSale && (
          <ReceiptOptions
            sale={lastSale}
            onClose={() =>
              setShowReceiptOptions(false)
            }
          />
        )}

    </main>

  </div>
);

};

export default Dashboard;
